// @ts-check
// Seletor de variáveis do e-mail (spec/email.md seções 5 e 19.3): busca e os grupos Contato, Lead, Escola e Especiais,
// só com os campos que o papel de quem edita lê (o catálogo vem do servidor). Depois de escolher, "Se estiver vazio,
// usar" grava o valor reserva: {{contact.name.firstName|família}}.
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import { signal } from '@preact/signals';
import { Dropdown, DropdownMenuItems, DropdownMenuSearchInput, DropdownMenuSectionLabel, MenuItem, DropdownMenuHeader } from '../../ui/dropdown.js';
import { Button } from '../../ui/button.js';
import { getEmailVariableCatalog } from '../../data/email-templates.js';
import { normalizeSearchText } from '../../lib/utils.js';
import { t, translateMetadataLabel } from '../../i18n/index.js';

/**
 * @typedef {import('../../data/email-templates.js').EmailVariableCatalog} EmailVariableCatalog
 * @typedef {import('../../data/email-templates.js').EmailVariableEntry} EmailVariableEntry
 */

export const VARIABLE_GROUPS = /** @type {const} */ (['contact', 'record', 'institution', 'special']);
const MAXIMUM_FALLBACK_LENGTH = 60;

/** @type {import('@preact/signals').Signal<EmailVariableCatalog | null>} */
export const emailVariableCatalogState = signal(null);
/** @type {Promise<EmailVariableCatalog | null> | null} */
let pendingCatalog = null;

/** Catálogo uma vez por aba (o papel não muda sem recarregar). */
export function loadEmailVariableCatalog() {
  if (emailVariableCatalogState.value) return Promise.resolve(emailVariableCatalogState.value);
  if (!pendingCatalog) {
    pendingCatalog = getEmailVariableCatalog()
      .then((catalog) => {
        emailVariableCatalogState.value = catalog;
        return catalog;
      })
      .catch(() => null)
      .finally(() => {
        pendingCatalog = null;
      });
  }
  return pendingCatalog;
}

export function useEmailVariableCatalog() {
  useEffect(() => {
    loadEmailVariableCatalog();
  }, []);
  return emailVariableCatalogState.value;
}

/** @param {EmailVariableEntry} entry */
export function variableEntryLabel(entry) {
  if (entry.labelKey) return t(entry.labelKey);
  const label = translateMetadataLabel(entry.label ?? entry.path);
  if (entry.type === 'FIRST_NAME') return t('emailTemplates.variables.firstNameOf', { field: translateMetadataLabel(entry.parentLabel ?? label) });
  if (entry.type === 'LAST_NAME') return t('emailTemplates.variables.lastNameOf', { field: translateMetadataLabel(entry.parentLabel ?? label) });
  return label;
}

/**
 * Rótulo legível de uma variável (o chip do texto formatado): grupo e campo. Fora do catálogo, o próprio caminho.
 * @param {string} path @param {EmailVariableCatalog | null} catalog
 */
export function variableDisplayLabel(path, catalog) {
  const root = path.split('.')[0];
  const groupKey = root === 'sender' || path === 'unsubscribeUrl' ? 'special' : root;
  const entry = catalog ? [...catalog.contact, ...catalog.record, ...catalog.institution, ...catalog.special].find((item) => item.path === path) : null;
  if (!entry) return path;
  const groupLabel = VARIABLE_GROUPS.includes(/** @type {any} */ (groupKey)) ? t(`emailTemplates.variables.groups.${groupKey}`) : '';
  return groupKey === 'institution' || groupKey === 'special' ? variableEntryLabel(entry) : `${groupLabel}: ${variableEntryLabel(entry)}`;
}

/** Valor reserva aceito na sintaxe: sem chaves nem barra vertical, até 60 caracteres. @param {string} value */
export const cleanVariableFallback = (value) => value.replace(/[{}|]/g, '').replace(/\s+/g, ' ').trim().slice(0, MAXIMUM_FALLBACK_LENGTH);

/** @param {string} path @param {string} fallback */
export const variableExpression = (path, fallback) => (fallback ? `{{${path}|${fallback}}}` : `{{${path}}}`);

/**
 * @param {{ anchorRef: { current: HTMLElement | null }, isOpen: boolean, onClose: () => void, onSelect: (expression: string, path: string) => void,
 *   catalog?: EmailVariableCatalog | null, groups?: ReadonlyArray<typeof VARIABLE_GROUPS[number]>, excludePaths?: ReadonlyArray<string>,
 *   placement?: 'bottom-start' | 'bottom-end' }} props
 *   excludePaths: no texto e no assunto, sem o endereço de descadastro (ele só faz sentido como link)
 */
export function VariablePicker({ anchorRef, isOpen, onClose, onSelect, catalog: providedCatalog, groups = VARIABLE_GROUPS, excludePaths = [], placement = 'bottom-start' }) {
  const loadedCatalog = useEmailVariableCatalog();
  const catalog = providedCatalog ?? loadedCatalog;
  const [search, setSearch] = useState('');
  const [chosen, setChosen] = useState(/** @type {EmailVariableEntry | null} */ (null));
  const [fallback, setFallback] = useState('');

  useEffect(() => {
    if (!isOpen) {
      setSearch('');
      setChosen(null);
      setFallback('');
    }
  }, [isOpen]);

  const query = normalizeSearchText(search);
  const visibleGroups = groups
    .map((group) => ({
      group,
      entries: (catalog?.[group] ?? []).filter((entry) => !excludePaths.includes(entry.path)
        && (!query || normalizeSearchText(`${variableEntryLabel(entry)} ${entry.path}`).includes(query))),
    }))
    .filter((item) => item.entries.length > 0);

  /** @param {EmailVariableEntry} entry */
  const choose = (entry) => {
    // o descadastro e o logo não têm valor reserva: são endereços que o envio sempre preenche
    if (entry.path === 'unsubscribeUrl') {
      onSelect(variableExpression(entry.path, ''), entry.path);
      onClose();
      return;
    }
    setChosen(entry);
  };
  const confirm = () => {
    if (!chosen) return;
    onSelect(variableExpression(chosen.path, cleanVariableFallback(fallback)), chosen.path);
    onClose();
  };

  return html`<${Dropdown} anchorRef=${anchorRef} isOpen=${isOpen} onClose=${onClose} width=${300} placement=${placement} role="dialog" className="email-variable-picker">
    ${chosen
      ? html`<${DropdownMenuHeader} title=${variableEntryLabel(chosen)} onBack=${() => setChosen(null)} />
          <form class="email-variable-fallback" onSubmit=${(/** @type {Event} */ event) => { event.preventDefault(); confirm(); }}>
            <label class="input-label" for="email-variable-fallback">${t('emailTemplates.variables.fallback')}</label>
            <input
              id="email-variable-fallback"
              class="text-input"
              value=${fallback}
              maxLength=${MAXIMUM_FALLBACK_LENGTH}
              placeholder=${t('emailTemplates.variables.fallbackPlaceholder')}
              ref=${(/** @type {HTMLInputElement | null} */ element) => element && !element.dataset.focused && (element.dataset.focused = 'true', requestAnimationFrame(() => element.focus()))}
              onInput=${(/** @type {Event} */ event) => setFallback(/** @type {HTMLInputElement} */ (event.currentTarget).value)}
            />
            <span class="input-hint">${t('emailTemplates.variables.fallbackHint')}</span>
            <code class="email-variable-expression">${variableExpression(chosen.path, cleanVariableFallback(fallback))}</code>
            <${Button} title=${t('emailTemplates.variables.insert')} size="small" accent="blue" type="submit" />
          </form>`
      : html`<${DropdownMenuSearchInput} value=${search} onChange=${setSearch} placeholder=${t('emailTemplates.variables.search')} />
          <${DropdownMenuItems} hasMaxHeight=${true}>
            ${catalog === null
              ? html`<div class="email-variable-empty">${t('emailTemplates.loading')}</div>`
              : visibleGroups.length === 0
                ? html`<div class="email-variable-empty">${t('emailTemplates.variables.empty')}</div>`
                : visibleGroups.map(({ group, entries }) => html`
                    <${DropdownMenuSectionLabel} label=${t(`emailTemplates.variables.groups.${group}`)} />
                    ${entries.map((entry) => html`<${MenuItem} key=${entry.path} text=${variableEntryLabel(entry)} contextualText=${entry.path} onClick=${() => choose(entry)} />`)}
                  `)}
          <//>`}
  <//>`;
}
